import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import RAPIER from "@dimforge/rapier3d-compat";
import { App } from "./App";
import { installXRLogShipper } from "./engine/render/xrLogShipper";
import "./styles.css";

// Install before any other module logs — captures every `[XR]` line
// from app boot onward and POSTs it to /api/xrlog.php so we can read
// what a Quest 3 user's session actually looks like server-side.
installXRLogShipper();

const root = document.getElementById("root");
if (!root) throw new Error("root element missing");

// Rapier ships its WASM as base64 inside the compat build, so this resolves
// without a network fetch. Done once before mount so every Stage / system
// can construct a `new RAPIER.World(...)` synchronously.
await RAPIER.init();

// Preload the canvas fonts (session 233). Tajawal + Inter come from
// Google Fonts via index.html — the browser fetches them async, but the
// 3D banner textures bake on first frame and would freeze the wrong
// font into their CanvasTextures. Waiting on `document.fonts.load`
// guarantees the in-canvas Arabic banners render in Tajawal 800 and
// the LCDs in Inter from frame zero. Falls back silently after 1.5 s
// so a Google Fonts outage never blocks boot.
if (typeof document !== "undefined" && document.fonts) {
  await Promise.race([
    Promise.all([
      document.fonts.load("800 56px Tajawal"),
      document.fonts.load("800 110px Tajawal"),
      // Session 346 — cabinet banners now render in Tajawal 600 + Inter 500
      // for a quieter, brand-consistent look.
      document.fonts.load("600 60px Tajawal"),
      document.fonts.load("500 24px Inter"),
      document.fonts.load("700 24px Inter")
    ]).catch(() => undefined),
    new Promise((r) => setTimeout(r, 1500))
  ]);
}

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>
);
